import React, { memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import RightArrowIcon from 'transfix-icons/build/RightArrowIcon'
import ShipmentsIcon from 'transfix-icons/build/ShipmentsIcon'
import Card from 'transfix-ui/components/Card'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { HOMEPAGE_BOX_SHADOW, TEST_IDS } from '../../../constants'

export interface IShipmentOffersCardMobileProps {
  /**
   * @default false
   */
  loading?: boolean
  /**
   * @default 0
   */
  offeredShipmentsCount: number
}

const useShipmentOffersCardMobileStyles = makeStyles(theme => ({
  card: {
    borderRadius: theme.spacing(0.5),
    padding: `${theme.spacing(2.5)} ${theme.spacing(2)}`,
    boxShadow: HOMEPAGE_BOX_SHADOW,
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexGrow: 1,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(0.25),
    '& > div': {
      lineHeight: 'normal',
    },
  },
  skeletonTitle: {
    height: 24,
    maxWidth: 200,
    width: '100%',
  },
  skeletonSubText: {
    height: 22,
    maxWidth: 250,
    width: '100%',
  },
}))

const ShipmentOffersCardMobile = memo<IShipmentOffersCardMobileProps>(
  ({ loading = false, offeredShipmentsCount = 0 }) => {
    const { trackEvent } = useFMSAnalytics()
    const styles = useShipmentOffersCardMobileStyles()
    const history = useHistory()

    const isOfferCountEmpty = !offeredShipmentsCount

    const iconColor = isOfferCountEmpty ? 'coolGray5' : 'activeBlue'

    const loadCount = isOfferCountEmpty ? 'No' : String(offeredShipmentsCount)
    const pluralText = offeredShipmentsCount === 1 ? 'offer' : 'offers'
    const loadsToAcceptText = `${loadCount} load ${pluralText} to accept`

    const loadInfoText = isOfferCountEmpty
      ? 'Bid on freight to receive offers'
      : 'Book offers before they expire'

    const cardClickHandler = useCallback(() => {
      history.push('/offers')
      trackEvent('Homepage', 'HOME_OFFERS_CLICKED_MOBILE_CARD')
    }, [history, trackEvent])

    return (
      <div onClick={cardClickHandler} role='button' tabIndex={0} aria-hidden='true'>
        <Card className={styles.card} data-test={TEST_IDS.shipmentOffersMobileCard}>
          <div className={styles.container}>
            <div className={styles.leftContent}>
              <ShipmentsIcon color={iconColor} size='xlarge' />
              {loading ? (
                <div className={styles.text}>
                  <Skeleton variant='rectangular' className={styles.skeletonTitle} />
                  <Skeleton variant='rectangular' className={styles.skeletonSubText} />
                </div>
              ) : (
                <div className={styles.text}>
                  <Typography variant='subtitle1' component='div'>
                    {loadsToAcceptText}
                  </Typography>
                  <Typography variant='body2' color='textSecondary' component='div'>
                    {loadInfoText}
                  </Typography>
                </div>
              )}
            </div>
            <RightArrowIcon color='coolGray4' size='large' />
          </div>
        </Card>
      </div>
    )
  }
)

export default ShipmentOffersCardMobile
